(() => {
  if (customElements.get('premium-sticky-purchase')) return;

  class PremiumStickyPurchase extends HTMLElement {
    connectedCallback() {
      this.region = document.getElementById(this.dataset.productRegion);
      this.button = this.querySelector('[data-sticky-submit]');
      if (!this.region || !this.button) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      PremiumStickyPurchase.current?.releaseLayout();
      PremiumStickyPurchase.current = this;
      this.actions = this.querySelector('.premium-sticky__actions');
      this.buyButton = this.querySelector('[data-sticky-buy]');
      this.image = this.querySelector('.premium-sticky__image');
      this.defaultImage ??= this.dataset.fallbackImage || this.image?.getAttribute('src');
      this.desktop = matchMedia('(min-width: 750px)');
      this.passedButtons = false;
      this.observedButtons = null;
      this.scheduleSync = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.scheduleLayout = () => {
        if (!this.layoutFrame) this.layoutFrame = requestAnimationFrame(() => {
          this.layoutFrame = null;
          this.updateLayout();
        });
      };

      this.button.addEventListener('click', () => {
        // Recheck the original button synchronously to prevent double submissions.
        const form = this.form;
        const submit = form?.querySelector('[type="submit"]');
        if (this.isDisabled(submit)) return;
        form.requestSubmit(submit);
        this.sync();
      }, { signal });

      this.buyButton?.addEventListener('click', () => {
        this.sync();
        if (this.buyButton.disabled || this.buyButton.hidden) return;
        // Shopify owns the payment UI (including closed shadow DOM wallets).
        // Keep its form, validation and checkout lifecycle intact.
        const payment = this.payment;
        payment.setAttribute('tabindex', '-1');
        payment.focus({ preventScroll: true });
        payment.scrollIntoView({
          block: 'center',
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }, { signal });

      // Watch the info column only, excluding gallery/zoom/video mutations.
      this.mutation = new MutationObserver(this.scheduleSync);
      this.mutation.observe(this.region.querySelector('.product__info-container') || this.region, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class'],
      });
      this.region.addEventListener('change', this.scheduleSync, { signal });
      this.desktop.addEventListener('change', () => this.updateVisibility(), { signal });
      // Include the page below the fold so jumping straight past the buttons
      // still crosses an intersection boundary, even if they started offscreen.
      this.visibility = new IntersectionObserver(([entry]) => {
        this.passedButtons = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.updateVisibility();
      }, { rootMargin: `0px 0px ${document.documentElement.scrollHeight}px 0px` });
      this.resize = new ResizeObserver(this.scheduleLayout);
      this.resize.observe(this);
      window.addEventListener('resize', this.scheduleLayout, { signal });
      this.sync();
      this.updateLayout();
      // The existing app loads asynchronously. Wait for it without polling.
      if (this.actions) customElements.whenDefined('gty-whatsapp-chat-button').then(() => {
        if (!signal.aborted && PremiumStickyPurchase.current === this) this.connectWhatsApp();
      });
    }

    get form() {
      const form = document.getElementById(this.dataset.formId);
      return this.region.contains(form) ? form : null;
    }

    isDisabled(submit) {
      return !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true';
    }

    updateVisibility() {
      const enabled = this.classList.contains(this.desktop.matches ? 'premium-sticky--desktop' : 'premium-sticky--mobile');
      this.hidden = !enabled || !this.passedButtons || !this.observedButtons;
      this.updateLayout();
    }

    connectWhatsApp() {
      const host = document.querySelector('gty-whatsapp-chat-button');
      const root = host?.shadowRoot;
      const wrapper = root?.querySelector('.gty-wacb-wrapper');
      const link = root?.querySelector('.gty-wacb-button');
      this.widgetObserver?.disconnect();
      if (!wrapper || !link) {
        this.widgetObserver = new MutationObserver(() => this.connectWhatsApp());
        this.widgetObserver.observe(root || document.body, { childList: true, subtree: true });
        return;
      }

      // Style the original app control; preserve its URL, events and shadow DOM.
      this.dataset.whatsappSide = wrapper.getBoundingClientRect().left + wrapper.offsetWidth / 2 < innerWidth / 2 ? 'left' : 'right';
      const style = document.createElement('style');
      style.textContent = `
        :host([data-premium-purchase-docked]) .gty-wacb-wrapper {
          top: auto !important;
          bottom: var(--premium-purchase-bottom) !important;
          left: var(--premium-purchase-left) !important;
          right: var(--premium-purchase-right) !important;
          transform: none !important;
          z-index: 5 !important;
          visibility: var(--premium-sticky-contact-visibility, visible) !important;
        }
        :host([data-premium-purchase-docked]) .gty-wacb-button {
          width: var(--premium-purchase-size) !important;
          height: var(--premium-purchase-size) !important;
        }`;
      root.appendChild(style);
      this.whatsapp = { host, style };
      this.updateLayout();
    }

    updateLayout() {
      if (PremiumStickyPurchase.current !== this) return;
      const height = Math.ceil(this.getBoundingClientRect().height);
      const pageStyle = document.documentElement.style;
      if (pageStyle.getPropertyValue('--premium-sticky-height') !== `${height}px`) {
        pageStyle.setProperty('--premium-sticky-height', `${height}px`);
      }
      if (!this.whatsapp) return;
      const { host } = this.whatsapp;
      if (this.hidden || !height) {
        host.removeAttribute('data-premium-purchase-docked');
        return;
      }
      const bounds = this.actions.getBoundingClientRect();
      const button = this.button.getBoundingClientRect();
      const size = parseFloat(getComputedStyle(this).getPropertyValue('--premium-sticky-contact-size')) || 48;
      const onLeft = this.dataset.whatsappSide === 'left';
      host.style.setProperty('--premium-purchase-size', `${size}px`);
      host.style.setProperty('--premium-purchase-left', onLeft ? `${bounds.left}px` : 'auto');
      host.style.setProperty('--premium-purchase-right', onLeft ? 'auto' : `${document.documentElement.clientWidth - bounds.right}px`);
      host.style.setProperty('--premium-purchase-bottom', `${innerHeight - button.top - (button.height + size) / 2}px`);
      host.setAttribute('data-premium-purchase-docked', '');
    }

    releaseLayout() {
      this.widgetObserver?.disconnect();
      if (this.whatsapp) {
        const { host, style } = this.whatsapp;
        host.removeAttribute('data-premium-purchase-docked');
        for (const key of ['size', 'left', 'right', 'bottom']) host.style.removeProperty(`--premium-purchase-${key}`);
        style.remove();
        this.whatsapp = null;
      }
      delete this.dataset.whatsappSide;
      if (PremiumStickyPurchase.current === this) {
        document.documentElement.style.removeProperty('--premium-sticky-height');
        PremiumStickyPurchase.current = null;
      }
    }

    sync() {
      const form = this.form;
      const submit = form?.querySelector('[type="submit"]');
      const disabled = this.isDisabled(submit);
      this.button.disabled = disabled;
      this.button.textContent = submit?.querySelector('span')?.textContent.trim() || this.button.textContent;
      this.button.setAttribute('aria-busy', String(submit?.classList.contains('loading') || false));

      this.payment = form?.querySelector('.shopify-payment-button, shopify-accelerated-checkout');
      if (this.buyButton) {
        this.buyButton.hidden = !this.payment || !!this.payment.closest('[hidden], .hidden');
        this.buyButton.disabled = disabled;
      }
      const secondary = (this.buyButton && !this.buyButton.hidden) || submit?.classList.contains('button--secondary');
      this.button.classList.toggle('button--secondary', !!secondary);
      this.button.classList.toggle('button--primary', !secondary);

      const price = this.region.querySelector('[id^="price-"]');
      const currentPrice = price?.querySelector('.premium-price__current') ||
        price?.querySelector('.price--on-sale .price-item--sale') || price?.querySelector('.price__regular .price-item');
      this.querySelector('[data-sticky-price]').textContent = price?.matches('.hidden, [hidden]') ? '' : currentPrice?.textContent.trim() || '';

      // The theme inserts the new picker before retiring the hidden old picker.
      const picker = this.region.querySelector('variant-selects');
      const options = [...(picker?.querySelectorAll('input:checked, option:checked') || [])].map(option => option.value);
      const optionText = this.querySelector('[data-sticky-option]');
      optionText.textContent = options.join(' / ');
      optionText.title = optionText.textContent;
      optionText.hidden = !options.length;

      const error = this.region.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
      this.querySelector('[data-sticky-error]').textContent = error?.textContent.trim() || '';
      this.updateImage();

      const buttons = submit && (form.querySelector('.product-form__buttons') || submit);
      if (buttons !== this.observedButtons) {
        this.visibility.disconnect();
        this.observedButtons = buttons;
        this.passedButtons = false;
        if (buttons) this.visibility.observe(buttons);
        this.updateVisibility();
      }
    }

    updateImage() {
      if (!this.image) return;
      const variantJson = this.region.querySelector('variant-selects [data-selected-variant]')?.textContent;
      if (variantJson === this.variantJson) return;
      this.variantJson = variantJson;
      let variant;
      try { variant = JSON.parse(variantJson); } catch { return; }
      const source = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || this.defaultImage;
      if (!source) return;
      const url = new URL(source, location.href);
      url.searchParams.set('width', '120');
      if (this.image.src !== url.href) {
        this.image.removeAttribute('srcset');
        this.image.src = url.href;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.mutation?.disconnect();
      this.visibility?.disconnect();
      this.resize?.disconnect();
      this.releaseLayout();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.layoutFrame);
      this.frame = null;
      this.layoutFrame = null;
    }
  }
  customElements.define('premium-sticky-purchase', PremiumStickyPurchase);
})();
